import * as React from 'react';
import GeoJSON from 'geojson';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import { formatDateTime, Spinner } from '@ixicare/ui';
import { t } from '~/i18n';
import MapWithInfo from '~/components/MapWithInfo';
import LocationTimelineControls from '~/components/LocationTimeline/LocationTimelineControls';
import LocationTimelineTrack, { placeLabel } from '~/components/LocationTimeline/LocationTimelineTrack';
import styles from '~/components/LocationTimeline/LocationTimeline.module.css';
import { TimelineWindow } from '~/components/LocationTimeline/useTimelineWindow';
import {
  clipGaps,
  findGaps,
  gapAt,
  isWithin,
  outsideWindows,
  pointAt,
  TimelineBand,
  TimelineEvent,
  TimelinePoint,
  TimelineSpan,
  toTimelinePoints,
} from '~/components/LocationTimeline/timeline.utils';
import { useLocationHistory } from '~hooks/location';
import MapPosition from './MapPosition';

type LocationTimelineMapProps = {
  deviceId: number;
  timeline: TimelineWindow;
  siteUuid?: string;
  type?: string;
  geofence?: GeoJSON.Feature<GeoJSON.Polygon>;
  events?: TimelineEvent[];
  bands?: TimelineBand[];
  initialLabel?: string;
  allowLive?: boolean;
  /** When set, only positions inside these stretches are shown; the rest of the track reads as hidden. */
  visibleWindows?: TimelineSpan[];
};

const LocationTimelineMap: React.FC<LocationTimelineMapProps> = ({
  deviceId,
  timeline,
  siteUuid,
  type,
  geofence,
  events = [],
  bands = [],
  initialLabel,
  allowLive,
  visibleWindows,
}) => {
  const [preview, setPreview] = React.useState<number | null>(null);
  const { locations, isLoading } = useLocationHistory(deviceId, timeline.start, timeline.end, timeline.live);

  const points = React.useMemo(() => {
    const all = toTimelinePoints(locations);
    return visibleWindows ? all.filter((point) => isWithin(visibleWindows, point.time)) : all;
  }, [locations, visibleWindows]);
  const gaps = React.useMemo(() => {
    const found = findGaps(points, timeline.now);
    return visibleWindows ? clipGaps(found, visibleWindows) : found;
  }, [points, timeline.now, visibleWindows]);
  const hidden = visibleWindows ? outsideWindows(visibleWindows, timeline.start, timeline.end) : [];

  const moment = preview ?? timeline.pinned ?? timeline.end;
  // Moving before the first position keeps the map on the last one shown, rather than unmounting and reloading it.
  // A refetch or a repeated live message hands over an equal position as a new object; keeping the one already
  // shown stops the map from being sent, and reframing on, a position it already has.
  const shown = React.useRef<TimelinePoint | undefined>(undefined);
  const isHidden = !!visibleWindows && !isWithin(visibleWindows, moment);
  const found = pointAt(points, moment);
  const point = isHidden ? undefined : !found || found.time === shown.current?.time ? shown.current : found;
  shown.current = point;
  const isStale = !!point && !!gapAt(gaps, moment);
  const isFollowingLive = timeline.live && preview === null;

  const info = point ? (
    <>
      {`${placeLabel(point)} (${formatDateTime(new Date(point.time))})`}
      {isStale && (
        <span className={styles.StaleNote}>
          {t('locationTimeline.noNewPositionSince', { time: format(point.time, 'HH:mm') })}
        </span>
      )}
    </>
  ) : null;

  return (
    <>
      {point ? (
        <div className={styles.MapArea}>
          <MapWithInfo info={info}>
            <MapPosition
              locationData={point.location}
              siteUuid={siteUuid}
              isStatic={false}
              type={type}
              geofence={geofence}
              animate={false}
            />
          </MapWithInfo>
          {isFollowingLive && (
            <div className={styles.MapChip}>
              <span className={styles.LiveDot} />
              {t('locationTimeline.live')}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.Empty}>
          {isHidden ? (
            t('locationTimeline.hiddenOutsideAlarms')
          ) : isLoading ? (
            <Spinner label={t('global.loading')} />
          ) : (
            t('locationTimeline.noPosition')
          )}
        </div>
      )}
      <LocationTimelineControls timeline={timeline} initialLabel={initialLabel} allowLive={allowLive} />
      <LocationTimelineTrack
        timeline={timeline}
        points={points}
        gaps={gaps}
        events={events}
        bands={bands}
        hidden={hidden}
        onPreview={setPreview}
      />
    </>
  );
};

export default observer(LocationTimelineMap);
